import { useCustomsDocsApi } from "@/Hooks/useCustomsDocsApi";
import { DocumentType } from "@freightsimple/generated-dashboard-openapi-client/models/DocumentType";
import { useOnce } from "@/Hooks/useOnce";
import { useState } from "react";
import { useShipmentService } from "@/Services/ShipmentService";
import { useCustomDocsDropzone } from "@/Shared/CustomDocs/Hooks/useCustomDocsDropzone";
import type { CurrentCustomsDocuments } from "@/Shared/CustomDocs/CustomComponents/CustomDocsUpdateSubmitBody";
import { CustomDocsUpdateSubmitBody } from "@/Shared/CustomDocs/CustomComponents/CustomDocsUpdateSubmitBody";
import type { UpdateCustomsDocsStageProps } from "@/Shared/CustomDocs/Dialogs/CustomDocsUpdateDialog";
import { CustomDocsUpdateStageType } from "@/Shared/CustomDocs/Types/CustomDocsUpdateStageType";

export function CustomDocsUpdateSubmitStage({
  shipmentId,
  onDone,
  setStage,
  mode,
}: UpdateCustomsDocsStageProps) {
  const customsDocsApi = useCustomsDocsApi();
  const [loading, setLoading] = useState(false);
  const [loadingSuccess, setLoadingSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const { files, setFiles, dropzoneState } = useCustomDocsDropzone();

  const [documents, setDocuments] =
    useState<CurrentCustomsDocuments>("loading");

  const { getDocuments, downloadDocument } = useShipmentService();

  async function refresh() {
    try {
      const response = await getDocuments(shipmentId);
      const customsDocs = response.filter(
        (d) => d.documentType === DocumentType.CustomsDocument,
      );
      setDocuments(customsDocs);
    } catch (e) {
      console.error(e);
      setDocuments("failed");
    }
  }

  useOnce(() => {
    refresh();
  });

  async function onUpload() {
    if (mode === undefined) {
      setErrorMessage("Something went wrong! Please try later.");
      return;
    }
    setLoadingSuccess(false);
    setErrorMessage(undefined);
    setLoading(true);
    try {
      await customsDocsApi.upload(shipmentId, mode, files);
      setLoadingSuccess(true);
      onDone();
    } catch (e) {
      console.error(`Failure uploading customs docs`, { e });
      setErrorMessage(
        "We couldn't upload your documents. Please try again, or contact us if it keeps failing.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <CustomDocsUpdateSubmitBody
      mode={mode}
      documents={documents}
      onDownload={(id) => downloadDocument(id, DocumentType.CustomsDocument)}
      files={files}
      setFiles={setFiles}
      dropzoneState={dropzoneState}
      errorMessage={errorMessage}
      uploading={loading}
      uploaded={loadingSuccess}
      onBack={() => setStage(CustomDocsUpdateStageType.SELECT_MODE)}
      onCancel={onDone}
      onUpload={onUpload}
    />
  );
}
